import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Lock, RefreshCw, Loader2, Mail, Briefcase, CreditCard, Crown, GraduationCap, Handshake, UserPlus, BookOpen, Sparkles, TrendingUp, Users, KeyRound, BookText, CalendarPlus, FileEdit } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { Container } from "@/components/Section";
import PartnershipDrawer, { StageBadge } from "@/components/PartnershipDrawer";
import CustomerJourney from "@/components/CustomerJourney";
import TeamManagement from "@/components/admin/TeamManagement";
import SystemStatusPanel from "@/components/admin/SystemStatusPanel";
import SecurityWarningBanner from "@/components/admin/SecurityWarningBanner";
import EventsManager from "@/components/admin/EventsManager";
import ContentEditor from "@/components/admin/ContentEditor";
import {
  authHeaders,
  canManageUsers,
  getStoredUser,
  loginWithEmail,
  loginWithLegacyPassword,
  logout as authLogout,
  refreshMe,
  ROLE_LABEL,
} from "@/lib/adminAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: "members", label: "Members", Icon: UserPlus },
  { key: "enquiries", label: "Enquiries", Icon: Briefcase },
  { key: "subscribers", label: "Subscribers", Icon: Mail },
  { key: "payments", label: "Payments", Icon: CreditCard },
  { key: "waitlist", label: "Membership", Icon: Crown },
  { key: "applications", label: "Applications", Icon: GraduationCap },
  { key: "partnerships", label: "Partnerships", Icon: Handshake },
  { key: "homeEducation", label: "Home Education", Icon: BookOpen },
  { key: "retreatBookings", label: "Ubuntu Retreat", Icon: Sparkles },
  { key: "events", label: "Events", Icon: CalendarPlus },
  { key: "content", label: "Content", Icon: FileEdit },
  { key: "team", label: "Team", Icon: Users },
];

export default function Admin() {
  const [loginMode, setLoginMode] = useState("legacy"); // 'legacy' is default for launch — 'email' is the multi-user path (available post-launch)
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [authed, setAuthed] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "enquiries";
  const [tab, setTab] = useState(initialTab);
  const initialContentPage = searchParams.get("page") || null;
  const initialContentGroup = searchParams.get("group") || null;
  const [data, setData] = useState({
    enquiries: [],
    subscribers: [],
    payments: [],
    waitlist: [],
    applications: [],
    partnerships: [],
    members: [],
    homeEducation: [],
    retreatBookings: [],
  });
  const [overview, setOverview] = useState(null);
  const [meta, setMeta] = useState(null);
  const [selectedPartnership, setSelectedPartnership] = useState(null);
  const [loading, setLoading] = useState(false);

  // Auto-restore session (bearer token or legacy password)
  useEffect(() => {
    (async () => {
      const stored = getStoredUser();
      if (stored) {
        const fresh = await refreshMe();
        setCurrentUser(fresh || stored);
        setAuthed(true);
        return;
      }
      // Legacy password fallback
      const legacy = localStorage.getItem("tok-admin-pass") || "";
      if (legacy) {
        try {
          await axios.post(`${API}/admin/verify`, { password: legacy });
          setCurrentUser({
            id: "legacy-admin",
            email: "legacy@admin.local",
            name: "Legacy Admin",
            role: "owner",
          });
          setAuthed(true);
        } catch {
          authLogout();
        }
      }
    })();
  }, []);

  const onLogin = async (e) => {
    e.preventDefault();
    setVerifying(true);
    try {
      if (loginMode === "email") {
        const user = await loginWithEmail(email.trim().toLowerCase(), pwd);
        setCurrentUser(user);
        toast.success(`Welcome, ${user.name}.`);
      } else {
        const user = await loginWithLegacyPassword(pwd);
        setCurrentUser(user);
        toast.success("Welcome back.");
      }
      setAuthed(true);
      setPwd("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed.");
    } finally {
      setVerifying(false);
    }
  };

  const doLogout = () => {
    authLogout();
    setAuthed(false);
    setCurrentUser(null);
    setData({ enquiries: [], subscribers: [], payments: [], waitlist: [], applications: [], partnerships: [], members: [], homeEducation: [], retreatBookings: [] });
    setOverview(null);
  };

  const load = useCallback(async () => {
    if (!authed) return;
    setLoading(true);
    try {
      const headers = authHeaders();
      const [enq, subs, pays, wait, apps, parts, metaRes, members, homeEd, retreat, ov] = await Promise.all([
        axios.get(`${API}/admin/enquiries`, { headers }),
        axios.get(`${API}/admin/subscribers`, { headers }),
        axios.get(`${API}/admin/payments`, { headers }),
        axios.get(`${API}/admin/membership-waitlist`, { headers }),
        axios.get(`${API}/admin/applications`, { headers }),
        axios.get(`${API}/admin/partnerships`, { headers }),
        axios.get(`${API}/admin/partnership-meta`, { headers }),
        axios.get(`${API}/admin/members`, { headers }),
        axios.get(`${API}/admin/home-education`, { headers }),
        axios.get(`${API}/admin/retreat-bookings`, { headers }),
        axios.get(`${API}/admin/leads-overview`, { headers }),
      ]);
      setData({
        enquiries: enq.data || [],
        subscribers: subs.data || [],
        payments: pays.data || [],
        waitlist: wait.data || [],
        applications: apps.data || [],
        partnerships: parts.data || [],
        members: members.data || [],
        homeEducation: homeEd.data || [],
        retreatBookings: retreat.data || [],
      });
      setMeta(metaRes.data || null);
      setOverview(ov.data || null);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Sign in again.");
        doLogout();
      } else {
        toast.error("Failed to load admin data.");
      }
    } finally {
      setLoading(false);
    }
  }, [authed]);

  useEffect(() => {
    if (authed) load();
  }, [authed, load]);

  if (!authed) {
    return (
      <div className="min-h-screen pt-32 pb-24 flex items-center">
        <Container className="max-w-md">
          <div className="border border-amber-400/30 bg-zinc-950 p-8">
            <Lock className="h-8 w-8 text-amber-400" />
            <p className="mt-6 text-[11px] uppercase tracking-[0.35em] text-amber-400 font-bold">
              Admin
            </p>
            <h1 className="mt-3 font-display font-black uppercase text-3xl leading-tight">
              Temple gate.
            </h1>
            <p className="mt-4 text-zinc-400 text-sm">
              {loginMode === "email"
                ? "Sign in with your team email."
                : "Enter the shared admin password (legacy)."}
            </p>
            <form
              onSubmit={onLogin}
              data-testid="admin-login-form"
              className="mt-8 space-y-4"
            >
              {loginMode === "email" && (
                <input
                  type="email"
                  data-testid="admin-email-input"
                  required
                  placeholder="you@templeofkulture.co.uk"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-black border border-white/10 px-4 py-3.5 text-white placeholder:text-zinc-600 focus:outline-none focus:border-amber-400 transition-colors"
                  autoComplete="email"
                />
              )}
              <input
                type="password"
                data-testid="admin-password-input"
                required
                placeholder={loginMode === "email" ? "Password" : "Admin password"}
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                className="w-full bg-black border border-white/10 px-4 py-3.5 text-white placeholder:text-zinc-600 focus:outline-none focus:border-amber-400 transition-colors"
                autoComplete="current-password"
              />
              <button
                type="submit"
                disabled={verifying}
                data-testid="admin-login-button"
                className="w-full inline-flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-black px-6 py-4 font-bold uppercase tracking-[0.25em] text-xs"
              >
                {verifying ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Verifying…
                  </>
                ) : (
                  "Enter"
                )}
              </button>
            </form>
            <button
              type="button"
              data-testid="admin-login-toggle"
              onClick={() => setLoginMode(loginMode === "legacy" ? "email" : "legacy")}
              className="mt-6 text-[11px] uppercase tracking-[0.3em] text-zinc-500 hover:text-amber-400 transition-colors"
            >
              {loginMode === "legacy"
                ? "Switch to team email sign-in →"
                : "← Use shared admin password"}
            </button>
          </div>
        </Container>
      </div>
    );
  }

  const current = data[tab] || [];

  return (
    <div data-testid="admin-page" className="min-h-screen pt-32 pb-24">
      <Container>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
          <div>
            <p className="text-[11px] uppercase tracking-[0.35em] text-amber-400 font-bold">
              Admin
              {currentUser && (
                <span
                  data-testid="admin-current-user"
                  className="ml-3 text-zinc-400 normal-case tracking-normal text-xs font-normal"
                >
                  · {currentUser.name || currentUser.email} ·{" "}
                  <span className="text-amber-400 uppercase tracking-[0.25em] font-bold">
                    {ROLE_LABEL[currentUser.role] || currentUser.role}
                  </span>
                </span>
              )}
            </p>
            <h1 className="mt-3 font-display font-black uppercase text-4xl sm:text-5xl leading-tight">
              Inside the temple.
            </h1>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <a
              href="/TOK_TEAM_OPERATIONS_GUIDE.md"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-ops-guide-link"
              className="inline-flex items-center gap-2 border border-white/10 hover:border-amber-400 hover:text-amber-400 px-5 py-3 text-xs uppercase tracking-[0.25em] font-bold transition-colors"
            >
              <BookText className="h-3.5 w-3.5" /> Ops guide
            </a>
            <button
              onClick={load}
              data-testid="admin-refresh-button"
              className="inline-flex items-center gap-2 border border-white/10 hover:border-amber-400 hover:text-amber-400 px-5 py-3 text-xs uppercase tracking-[0.25em] font-bold transition-colors"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={doLogout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 border border-white/10 hover:border-red-400 hover:text-red-400 px-5 py-3 text-xs uppercase tracking-[0.25em] font-bold transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>

        <CustomerJourney />

        {/* Security warnings — default password / weak secret / mocked email */}
        <SecurityWarningBanner />

        {/* System status — green/amber/red snapshot of every integration */}
        <div className="mb-8">
          <SystemStatusPanel />
        </div>

        {overview && (
          <div
            data-testid="leads-overview"
            className="mb-10 border border-amber-400/20 bg-gradient-to-br from-amber-400/5 to-transparent p-6 sm:p-8"
          >
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <p className="tok-eyebrow flex items-center gap-2">
                  <TrendingUp className="h-3.5 w-3.5" />
                  Lead intelligence
                </p>
                <h3 className="tok-headline-md mt-2 text-xl">
                  Last 7 days · last 30 days
                </h3>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
              {Object.entries(overview.counts || {}).map(([k, v]) => (
                <div
                  key={k}
                  data-testid={`leads-card-${k.replace(/_/g, '-')}`}
                  className="border border-white/5 bg-zinc-950 p-3"
                >
                  <p className="text-[9px] uppercase tracking-[0.28em] font-bold text-zinc-500">
                    {k.replace(/_/g, " ")}
                  </p>
                  <p className="mt-2 font-display font-black text-2xl">
                    {v.last_7_days}
                    <span className="text-[10px] text-zinc-500 ml-1 font-sans font-normal tracking-normal">
                      / {v.last_30_days}
                    </span>
                  </p>
                  <p className="text-[10px] text-zinc-500 mt-1">
                    {v.total} total
                  </p>
                </div>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-6 mt-8">
              <div>
                <p className="tok-eyebrow-sm mb-3">Top referrals</p>
                <ul className="space-y-1.5">
                  {(overview.by_referral_source || []).slice(0, 7).map((r) => (
                    <li
                      key={r.source}
                      className="flex items-center justify-between text-sm border-b border-white/5 pb-1.5"
                    >
                      <span className="text-zinc-300">{r.source || "—"}</span>
                      <span className="font-mono text-xs text-amber-400">
                        {r.count}
                      </span>
                    </li>
                  ))}
                  {(!overview.by_referral_source || !overview.by_referral_source.length) && (
                    <li className="text-zinc-500 text-sm">No referral data yet.</li>
                  )}
                </ul>
              </div>
              <div>
                <p className="tok-eyebrow-sm mb-3">Top converting pages</p>
                <ul className="space-y-1.5">
                  {(overview.by_page_source || []).slice(0, 7).map((p) => (
                    <li
                      key={p.page}
                      className="flex items-center justify-between text-sm border-b border-white/5 pb-1.5"
                    >
                      <span className="text-zinc-300 truncate max-w-[260px]" title={p.page}>
                        {p.page}
                      </span>
                      <span className="font-mono text-xs text-amber-400">
                        {p.count}
                      </span>
                    </li>
                  ))}
                  {(!overview.by_page_source || !overview.by_page_source.length) && (
                    <li className="text-zinc-500 text-sm">No page-source data yet.</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 xl:grid-cols-9 gap-3 mb-10">
          {TABS.map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`admin-tab-${key}`}
              className={`p-5 text-left border transition-colors ${
                tab === key
                  ? "border-amber-400 bg-amber-400/5"
                  : "border-white/5 bg-zinc-950 hover:border-amber-400/30"
              }`}
            >
              <Icon className={`h-5 w-5 ${tab === key ? "text-amber-400" : "text-zinc-400"}`} />
              <p className="mt-3 text-[10px] uppercase tracking-[0.3em] font-bold text-zinc-400">
                {label}
              </p>
              <p className="mt-2 font-display font-black text-2xl sm:text-3xl">
                {key === "team" || key === "events" || key === "content" ? "—" : data[key]?.length ?? 0}
              </p>
            </button>
          ))}
        </div>

        {tab === "team" && (
          <TeamManagement currentUser={currentUser} />
        )}

        {tab === "events" && (
          <EventsManager currentUser={currentUser} />
        )}

        {tab === "content" && (
          <ContentEditor
            initialPage={initialContentPage}
            initialGroup={initialContentGroup}
          />
        )}

        {tab !== "team" && tab !== "events" && tab !== "content" && loading && (
          <div className="py-16 flex items-center justify-center gap-3 text-zinc-500">
            <Loader2 className="h-5 w-5 animate-spin text-amber-400" /> Loading…
          </div>
        )}

        {tab !== "team" && tab !== "events" && tab !== "content" && !loading && current.length === 0 && (
          <p data-testid="admin-empty" className="text-zinc-400 py-12">
            Nothing here yet.
          </p>
        )}

        {tab !== "team" && tab !== "events" && tab !== "content" && !loading && current.length > 0 && (
          <>
            <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs text-zinc-500">
                {current.length} row{current.length === 1 ? "" : "s"} ·{" "}
                <span className="text-amber-400">{tab}</span>
              </p>
              <CsvExportButton tab={tab} />
            </div>
            <div className="border border-white/5 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-950 text-amber-400 text-[10px] uppercase tracking-[0.25em] font-bold">
                <tr>
                  {tab === "enquiries" && (
                    <>
                      <Th>Created</Th>
                      <Th>Organisation</Th>
                      <Th>Contact</Th>
                      <Th>Email</Th>
                      <Th>Type</Th>
                      <Th>Message</Th>
                    </>
                  )}
                  {tab === "members" && (
                    <>
                      <Th>Created</Th>
                      <Th>Name</Th>
                      <Th>Email</Th>
                      <Th>Age group</Th>
                      <Th>Interest</Th>
                      <Th>Location</Th>
                      <Th>Notes</Th>
                    </>
                  )}
                  {tab === "homeEducation" && (
                    <>
                      <Th>Created</Th>
                      <Th>Parent</Th>
                      <Th>Email</Th>
                      <Th>Children</Th>
                      <Th>Ages</Th>
                      <Th>Day</Th>
                      <Th>Funded?</Th>
                      <Th>Referral</Th>
                      <Th>Interests</Th>
                    </>
                  )}
                  {tab === "retreatBookings" && (
                    <>
                      <Th>Created</Th>
                      <Th>Name</Th>
                      <Th>Email</Th>
                      <Th>Audience</Th>
                      <Th>Party</Th>
                      <Th>Month</Th>
                      <Th>Referral</Th>
                      <Th>Interests</Th>
                    </>
                  )}
                  {tab === "subscribers" && (
                    <>
                      <Th>Created</Th>
                      <Th>Email</Th>
                      <Th>Name</Th>
                    </>
                  )}
                  {tab === "payments" && (
                    <>
                      <Th>Created</Th>
                      <Th>Event</Th>
                      <Th>Amount</Th>
                      <Th>Status</Th>
                      <Th>Email</Th>
                      <Th>Session</Th>
                    </>
                  )}
                  {tab === "waitlist" && (
                    <>
                      <Th>Created</Th>
                      <Th>Email</Th>
                      <Th>Name</Th>
                      <Th>Interest</Th>
                    </>
                  )}
                  {tab === "applications" && (
                    <>
                      <Th>Created</Th>
                      <Th>Name</Th>
                      <Th>Email</Th>
                      <Th>Age</Th>
                      <Th>City</Th>
                      <Th>Interests</Th>
                      <Th>Why</Th>
                    </>
                  )}
                  {tab === "partnerships" && (
                    <>
                      <Th>Created</Th>
                      <Th>Stage</Th>
                      <Th>Organisation</Th>
                      <Th>Type</Th>
                      <Th>Contact</Th>
                      <Th>Tags</Th>
                      <Th>Funding</Th>
                      <Th>Referred</Th>
                      <Th>Action</Th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {current.map((row, i) => (
                  <tr
                    key={row.id || row.session_id || i}
                    data-testid={`admin-row-${tab}-${i}`}
                    className="hover:bg-zinc-950/50"
                  >
                    {tab === "enquiries" && (
                      <>
                        <Td>{fmt(row.created_at)}</Td>
                        <Td>{row.organisation}</Td>
                        <Td>{row.contact_name}</Td>
                        <Td>{row.email}</Td>
                        <Td>{row.experience_type}</Td>
                        <Td className="max-w-md truncate" title={row.message}>
                          {row.message}
                        </Td>
                      </>
                    )}
                    {tab === "members" && (
                      <>
                        <Td>{fmt(row.created_at)}</Td>
                        <Td>{row.name}</Td>
                        <Td>{row.email}</Td>
                        <Td className="font-mono text-xs">{row.age_group}</Td>
                        <Td className="max-w-[220px] truncate" title={row.interest_area}>{row.interest_area}</Td>
                        <Td>{row.location || "—"}</Td>
                        <Td className="max-w-md truncate" title={row.notes}>{row.notes || "—"}</Td>
                      </>
                    )}
                    {tab === "homeEducation" && (
                      <>
                        <Td>{fmt(row.created_at)}</Td>
                        <Td>{row.parent_name}</Td>
                        <Td>{row.email}</Td>
                        <Td className="font-mono text-xs">{row.children_count}</Td>
                        <Td>{row.age_range}</Td>
                        <Td className="font-mono text-xs uppercase">{row.preferred_day}</Td>
                        <Td>
                          {row.funded_place_interest ? (
                            <span className="px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-bold bg-amber-400/20 text-amber-300 border border-amber-400/40">Yes</span>
                          ) : <span className="text-zinc-500">No</span>}
                        </Td>
                        <Td className="text-xs text-zinc-400">{row.referral_source || "—"}</Td>
                        <Td className="max-w-md truncate" title={(row.interests || []).join(", ")}>
                          {(row.interests || []).join(", ") || "—"}
                        </Td>
                      </>
                    )}
                    {tab === "retreatBookings" && (
                      <>
                        <Td>{fmt(row.created_at)}</Td>
                        <Td>{row.name}</Td>
                        <Td>{row.email}</Td>
                        <Td className="font-mono text-xs uppercase">{row.audience_type}</Td>
                        <Td className="font-mono text-xs">{row.party_size}</Td>
                        <Td>{row.preferred_month || "—"}</Td>
                        <Td className="text-xs text-zinc-400">{row.referral_source || "—"}</Td>
                        <Td className="max-w-md truncate" title={(row.interests || []).join(", ")}>
                          {(row.interests || []).join(", ") || "—"}
                        </Td>
                      </>
                    )}
                    {tab === "subscribers" && (
                      <>
                        <Td>{fmt(row.created_at)}</Td>
                        <Td>{row.email}</Td>
                        <Td>{row.name || "—"}</Td>
                      </>
                    )}
                    {tab === "payments" && (
                      <>
                        <Td>{fmt(row.created_at)}</Td>
                        <Td>{row.event_title}</Td>
                        <Td>£{Number(row.amount_total_gbp || 0).toFixed(2)}</Td>
                        <Td>
                          <span
                            className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-bold ${
                              row.payment_status === "paid"
                                ? "bg-amber-400 text-black"
                                : "bg-zinc-800 text-zinc-300"
                            }`}
                          >
                            {row.payment_status}
                          </span>
                        </Td>
                        <Td>{row.email || "—"}</Td>
                        <Td className="max-w-[140px] truncate font-mono text-xs text-zinc-500" title={row.session_id}>
                          {row.session_id}
                        </Td>
                      </>
                    )}
                    {tab === "waitlist" && (
                      <>
                        <Td>{fmt(row.created_at)}</Td>
                        <Td>{row.email}</Td>
                        <Td>{row.name || "—"}</Td>
                        <Td className="max-w-md truncate" title={row.interest || ""}>
                          {row.interest || "—"}
                        </Td>
                      </>
                    )}
                    {tab === "applications" && (
                      <>
                        <Td>{fmt(row.created_at)}</Td>
                        <Td>{[row.first_name, row.last_name].filter(Boolean).join(" ")}</Td>
                        <Td>{row.email}</Td>
                        <Td>{row.age || "—"}</Td>
                        <Td>{row.city || "—"}</Td>
                        <Td className="max-w-[180px] truncate" title={(row.interests || []).join(", ")}>
                          {(row.interests || []).join(", ") || "—"}
                        </Td>
                        <Td className="max-w-md truncate" title={row.why_join}>
                          {row.why_join}
                        </Td>
                      </>
                    )}
                    {tab === "partnerships" && (
                      <>
                        <Td>{fmt(row.created_at)}</Td>
                        <Td><StageBadge stage={row.stage || "new_enquiry"} /></Td>
                        <Td data-testid={`admin-partnership-row-${row.id}`}>
                          <div className="font-bold">{row.organisation}</div>
                          <div className="text-xs text-zinc-500 truncate max-w-[220px]" title={row.email}>{row.email}</div>
                        </Td>
                        <Td>{row.organisation_type}</Td>
                        <Td>
                          <div>{row.contact_name}</div>
                          {row.role && <div className="text-xs text-zinc-500">{row.role}</div>}
                        </Td>
                        <Td className="max-w-[180px]">
                          <div className="flex flex-wrap gap-1">
                            {(row.programme_tags || []).slice(0, 3).map((t) => (
                              <span key={t} className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.15em] bg-amber-400/10 text-amber-300 border border-amber-400/30">
                                {t}
                              </span>
                            ))}
                            {(row.programme_tags || []).length > 3 && (
                              <span className="text-[10px] text-zinc-500">+{(row.programme_tags || []).length - 3}</span>
                            )}
                            {(row.programme_tags || []).length === 0 && <span className="text-zinc-600 text-xs">—</span>}
                          </div>
                        </Td>
                        <Td className="font-mono text-xs">
                          {row.funding_value_gbp ? `${row.funding_currency || "GBP"} ${Number(row.funding_value_gbp).toLocaleString()}` : "—"}
                        </Td>
                        <Td className="font-mono text-xs">{row.referred_participants_count || 0}</Td>
                        <Td>
                          <button
                            onClick={() => setSelectedPartnership(row)}
                            data-testid={`admin-partnership-open-${row.id}`}
                            className="border border-amber-400/40 hover:bg-amber-400 hover:text-black text-amber-400 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold transition-colors"
                          >
                            Open
                          </button>
                        </Td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </Container>

      {selectedPartnership && (
        <PartnershipDrawer
          partnership={selectedPartnership}
          meta={meta}
          onClose={() => setSelectedPartnership(null)}
          onUpdated={(updated) => {
            setSelectedPartnership(updated);
            setData((d) => ({
              ...d,
              partnerships: d.partnerships.map((p) =>
                p.id === updated.id ? updated : p
              ),
            }));
          }}
        />
      )}
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left px-4 py-3 whitespace-nowrap">{children}</th>;
}
function Td({ children, className = "", ...rest }) {
  return (
    <td className={`px-4 py-3 text-zinc-200 align-top ${className}`} {...rest}>
      {children}
    </td>
  );
}
function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

// Map admin-tab keys to backend collection names for CSV export.
const TAB_TO_COLLECTION = {
  members: "members",
  enquiries: "enquiries",
  subscribers: "subscribers",
  payments: "payments",
  waitlist: "membership_waitlist",
  applications: "applications",
  partnerships: "partnerships",
  homeEducation: "home_education",
  retreatBookings: "retreat_bookings",
};

function CsvExportButton({ tab }) {
  const collection = TAB_TO_COLLECTION[tab];
  if (!collection) return null;
  const onClick = async () => {
    try {
      const res = await axios.get(`${API}/admin/export/${collection}.csv`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tok-${collection}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${collection}.csv`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed.");
    }
  };
  return (
    <button
      onClick={onClick}
      data-testid={`csv-export-${tab}`}
      className="inline-flex items-center gap-2 border border-amber-400/40 hover:border-amber-400 hover:text-amber-400 text-zinc-300 px-3 py-2 text-[10px] uppercase tracking-[0.22em] font-bold transition-colors"
    >
      Export CSV
    </button>
  );
}

